import React from 'react';
import { Button, Alert } from 'reactstrap';
import DeleteAccountModal from './SubscriptionDeleteAccountModal';
import CancelModal from './SubscriptionCancelModal';
import { formatDateWithTimezone, daysRemaining } from '../../utils/dateUtils';
import { capitalizeFirst } from '../../utils/stringUtils';

function DetailRow({ label, value, valueClass }) {
  return (
    <>
      <dt>{label}</dt>
      <dd className={valueClass || undefined}>{value}</dd>
    </>
  );
}

class Detail extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      deleteAccountModalOpen: false,
      cancelModalOpen: false,
    };
  }

  openDeleteAccountModal = () => this.setState({ deleteAccountModalOpen: true });
  closeDeleteAccountModal = () => this.setState({ deleteAccountModalOpen: false });

  openCancelModal = () => {
    this.props.onClearError();
    this.setState({ cancelModalOpen: true });
  };
  closeCancelModal = () => {
    this.setState({ cancelModalOpen: false });
    this.props.onClearError();
  };

  handleConfirmCancel = (selectedReasonId) => {
    const { membership, onCancel } = this.props;
    onCancel(membership.id, selectedReasonId);
    this.closeCancelModal();
  };

  handleReactivate = () => {
    const { membership, onReactivate } = this.props;
    onReactivate(membership.id);
  };

  handleDeleteAccount = () => {
    const { onDeleteAccount } = this.props;
    onDeleteAccount();
    this.closeDeleteAccountModal();
  };

  render() {
    const { membership, cancellationReasons, membershipOptions, error, cancelInProgress, onGoToUpdateBilling, onGoToUpgrade } = this.props;
    const { deleteAccountModalOpen, cancelModalOpen } = this.state;
    const {
      productName,
      billingFrequency,
      nextBillDate,
      nextBillAmount,
      billing,
      status,
      membershipEditable,
    } = membership;

    const card = billing && billing.card;
    const isPendingCancellation = status === 'pending-cancellation';
    const isPastDue = status === 'past_due';
    const isActive = status === 'active';
    const days = isPendingCancellation ? daysRemaining(nextBillDate) : 0;

    const canUpgrade = isActive && membershipOptions && membershipOptions.length > 1 &&
      (productName === 'Monthly' || productName === 'Yearly');
    const upgradeLabel = productName === 'Monthly' ? 'Switch to Yearly' : 'Switch to Lifetime';
    const upgradeBlurb = productName === 'Monthly'
      ? 'Switch to Yearly TODAY and save up to 50%!'
      : 'Switch to Lifetime TODAY!';

    if (!membershipEditable) {
      return (
        <div>
          <h1>Manage Subscription</h1>
          <h3 className="mt-3 mb-4">You are on the {productName} Plan</h3>
          <p className="mb-4">
            It looks like you are currently signed up on a non-renewing lifetime membership plan.
            You will never be charged again.
          </p>
        </div>
      );
    }

    const { onLogout } = this.props;

    return (
      <div className="manage-subscription">
        <header className="manage-subscription-header">
          <h1 className="manage-subscription-title">Manage your subscription</h1>
          <p className="manage-subscription-subtitle">
            View your plan, update payment method, or cancel. Changes take effect at the start of your next billing cycle.
          </p>
        </header>

        {error && <Alert color="danger">{error}</Alert>}

        {isPendingCancellation && (
          <Alert color="warning">
            You have canceled your membership. You will only have access to Macrostax for{' '}
            <strong>{days} more days</strong>.
          </Alert>
        )}

        {isPastDue && (
          <Alert color="danger">
            Your membership is past due. Please update your billing information to maintain
            Macrostax access.
          </Alert>
        )}

        <section className="manage-subscription-main" aria-label="Your plan">
          <div className="manage-subscription-main-grid">
            <div className="manage-subscription-plan-header">
              <span className="manage-subscription-plan-label">Your plan</span>
              <span className="manage-subscription-plan-name">
                {capitalizeFirst(productName || '')}
                {isPendingCancellation && (
                  <span className="manage-subscription-plan-badge text-danger">
                    <em className="fas fa-exclamation-triangle manage-subscription-plan-badge-icon" aria-hidden />
                    {' '}Cancellation confirmed
                  </span>
                )}
              </span>
            </div>
            <div className="manage-subscription-payment-header">
              <h3 className="manage-subscription-subsection-title">Billing information</h3>
              <p className="manage-subscription-note text-muted small">
                We use Stripe to process payments. All information is secure and confidential.
              </p>
            </div>
            <div className="manage-subscription-plan">
              <dl className="manage-subscription-details">
                {isPendingCancellation && (
                  <DetailRow label="Account End Date" value={formatDateWithTimezone(nextBillDate, undefined, 'MMM D, YYYY') || 'None'} valueClass="text-danger" />
                )}
                {nextBillAmount && (
                  <DetailRow label="Price" value={`$${nextBillAmount}`} />
                )}
                <DetailRow label="Next Bill" value={isPendingCancellation ? 'None' : (formatDateWithTimezone(nextBillDate, undefined, 'MMM D, YYYY') || 'None')} />
                {billingFrequency && billingFrequency !== 'lifetime' && (
                  <DetailRow label="Billing Cycle" value={`Every ${billingFrequency}`} />
                )}
              </dl>
            </div>

            <div className="manage-subscription-payment">
              {card && card.last4 ? (
                <>
                  <dl className="manage-subscription-details">
                    <DetailRow label="Card" value={`····${card.last4}`} />
                    {card.expDate && (
                      <DetailRow label="Expires" value={formatDateWithTimezone(card.expDate, undefined, 'MMM, YYYY')} />
                    )}
                  </dl>
                  <div className="manage-subscription-payment-btn d-flex align-items-center cursor-pointer mt-3" onClick={onGoToUpdateBilling}>
                    Update Billing Information
                    <img src="/img/arrow-right.svg" className="ml-2" alt="" />
                  </div>
                </>
              ) : (
                <div className="manage-subscription-payment-btn d-flex align-items-center cursor-pointer mt-2" onClick={onGoToUpdateBilling}>
                  Update Billing Information
                  <img src="/img/arrow-right.svg" className="ml-2" alt="" />
                </div>
              )}
            </div>
          </div>

          {isPendingCancellation && (
            <div className="manage-subscription-reactivate">
              <Button color="primary" size="lg" block onClick={this.handleReactivate}>
                Reactivate Account
              </Button>
            </div>
          )}

          {canUpgrade && (
            <div className="manage-subscription-upgrade">
              <p className="manage-subscription-upgrade-text text-muted small mb-2">
                You're doing great! Why not stick around?{' '}
                {upgradeBlurb}
              </p>
              <Button color="primary" size="lg" block onClick={onGoToUpgrade}>
                {upgradeLabel}
              </Button>
            </div>
          )}
        </section>

        <section className="manage-subscription-actions" aria-label={isActive ? 'Cancel Account' : 'Account'}>
          {isActive && (
            <>
              <h2 className="manage-subscription-actions-title">Cancel Account</h2>
              <div className="manage-subscription-actions-block">
                <p className="text-muted small">
                  We&apos;re sorry to see you go but you&apos;re welcome back any time!
                </p>
                {cancelInProgress ? (
                  <div className="d-flex align-items-center justify-content-center py-2">
                    <span className="spinner-border spinner-border-sm text-primary mr-2" role="status" aria-hidden />
                    <span className="text-muted">Canceling your subscription…</span>
                  </div>
                ) : (
                  <Button outline color="primary" size="lg" block onClick={this.openCancelModal}>
                    Stop Auto Renew
                  </Button>
                )}
              </div>
            </>
          )}
          <div className="manage-subscription-delete">
            <Button color="link" size="sm" className="text-danger p-0" onClick={this.openDeleteAccountModal}>
              Delete Account
            </Button>
          </div>
        </section>


        <CancelModal
          isOpen={cancelModalOpen}
          cancellationReasons={cancellationReasons || []}
          onClose={this.closeCancelModal}
          onConfirm={this.handleConfirmCancel}
        />
        <DeleteAccountModal
          isOpen={deleteAccountModalOpen}
          onClose={this.closeDeleteAccountModal}
          onDelete={this.handleDeleteAccount}
        />
      </div>
    );
  }
}

export default Detail;
